import { Component, Input } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { MiddleTruncateComponent } from './middle-truncate.component';

/**
 * A list-table cell that shows a value and an icon that copies it, plus an icon that opens `href` when one is given. A
 * value too wide for the cell is cut with an ellipsis, and `keepFrom` moves the cut to the middle so the end stays
 * readable. The icons stop their clicks, so they do not also open the row.
 */
@Component({
    selector: 'app-copy-cell',
    standalone: true,
    imports: [FaIconComponent, MiddleTruncateComponent],
    template: `
        @if (value) {
        <span class="fd-copy-cell">
            <app-middle-truncate class="fd-copy-cell__text" [value]="value" [keepFrom]="keepFrom"></app-middle-truncate>
            <button
                type="button"
                class="fd-copy-cell__copy"
                [id]="cellId"
                (click)="copy(); $event.stopPropagation()"
                [attr.aria-label]="label"
                [title]="title"
            >
                <fa-icon class="fd-copy-cell__icon" [icon]="faCopy"></fa-icon>
            </button>
            @if (href) {
            <a
                class="fd-copy-cell__copy"
                [id]="cellId ? cellId + 'Open' : ''"
                [href]="href"
                target="_blank"
                rel="noopener noreferrer"
                (click)="$event.stopPropagation()"
                [attr.aria-label]="'Open ' + value"
                title="Open in a new tab"
            >
                <svg class="fd-copy-cell__open-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M18 13.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5.5M15 3h6v6M10.5 13.5 21 3" />
                </svg>
            </a>
            }
        </span>
        } @else {
        <span class="fd-copy-cell__muted">—</span>
        }
    `,
    styles: [
        `
            .fd-copy-cell {
                align-items: center;
                display: flex;
                font-family: monospace;
                font-size: 0.75rem;
                gap: 0.375rem;
                width: 100%;
            }

            /* Shrinks before the icon does, so the icon is never drawn over. */
            .fd-copy-cell__text {
                flex: 0 1 auto;
            }

            /* One fixed box for the copy button and the open link, so both hover the same. */
            .fd-copy-cell__copy {
                align-items: center;
                background: none;
                border: none;
                border-radius: 0.25rem;
                box-sizing: border-box;
                color: var(--primary);
                cursor: pointer;
                display: flex;
                flex: 0 0 auto;
                height: 1.25rem;
                justify-content: center;
                line-height: 1;
                padding: 0;
                text-decoration: none;
                width: 1.375rem;
            }

            /* A button does not inherit the font size and a link does, so both icons get one size here. As a flex
               box, the icon drops the SVG baseline offset and sits in the middle. */
            .fd-copy-cell__icon {
                display: flex;
                font-size: 0.875rem;
                line-height: 1;
            }

            /* The open icon in the outline style of the copy icon, which no free Font Awesome icon offers. The stroke
               matches the copy icon's line weight at this size. */
            .fd-copy-cell__open-icon {
                fill: none;
                height: 0.875rem;
                stroke: currentColor;
                stroke-linecap: round;
                stroke-linejoin: round;
                stroke-width: 2.25;
                width: 0.875rem;
            }

            .fd-copy-cell__copy:hover {
                background-color: var(--hoverBackground);
            }

            .fd-copy-cell__muted {
                color: var(--placeholder);
            }
        `,
    ],
})
export class CopyCellComponent {
    readonly faCopy = faCopy;

    @Input() value = '';
    /** What the copy button says to a screen reader. */
    @Input() label = '';
    @Input() title = 'Copy';
    /** The id of the copy button, for specs and e2e selectors. */
    @Input() cellId = '';
    /** Keeps the value from the last `keepFrom` on always in view. See {@link MiddleTruncateComponent}. */
    @Input() keepFrom = '';
    /** A link the cell also opens in a new tab, or '' for none. */
    @Input() href = '';
    @Input() copy: () => void = () => undefined;
}
